import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchNovels, fetchSettings } from "@/lib/api";
import { BookOpen, User, Calendar, ChevronRight } from "lucide-react";
import { getImageProps, getImageUrl, splitSlug } from "@/lib/utils";

export const Route = createFileRoute("/novel/")({
  loader: async ({ context: { queryClient } }) => {
    const [novels, settings] = await Promise.all([
      queryClient.ensureQueryData({ queryKey: ["novels"], queryFn: fetchNovels }),
      queryClient.ensureQueryData({ queryKey: ["settings"], queryFn: fetchSettings }),
    ]);
    return { novels, settings };
  },
  head: ({ loaderData }) => {
    const { settings } = loaderData as any;
    const siteName = settings?.site_name || "Lihar Aksara";
    const canonicalUrl = "https://liharaksara.web.id/novel";
    const title = `Katalog Novel — ${siteName}`;
    const description = `Temukan novel-novel terpilih karya penulis terbaik yang diterbitkan oleh ${siteName}.`;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: Novels,
});

function Novels() {
  const { data: novels } = useSuspenseQuery({ queryKey: ["novels"], queryFn: fetchNovels }) as any;

  return (
    <div className="container-editorial py-16 md:py-32">
      <div className="mb-24">
        <div className="text-[10px] uppercase tracking-[0.5em] text-accent font-bold mb-6">
          Koleksi Terpilih
        </div>
        <h1 className="font-serif text-5xl md:text-8xl font-medium tracking-tight leading-[0.95] mb-8">
          Katalog Novel & Seri
        </h1>
        <p className="text-xl md:text-2xl text-muted-foreground font-serif max-w-3xl italic leading-relaxed">
          Karya fiksi panjang yang kami kurasi dengan ketelitian. Cerita yang membangun semesta dan menyentuh jiwa, tersaji dalam setiap babnya.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 md:gap-x-12 md:gap-y-20">
        {novels?.map((novel: any) => (
          <Link 
            key={novel.id} 
            to="/novel/$id/$slug" 
            params={splitSlug(novel.slug || "")} 
            className="group block"
          >
            <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-muted mb-8 shadow-2xl shadow-black/40 group-hover:shadow-accent/10 transition-all duration-700 ease-out border border-white/5">
              <img
                {...getImageProps(novel.coverImage)}
                alt={novel.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
                 <div className="flex items-center gap-3 text-white">
                    <BookOpen size={20} className="  text-accent" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">{novel.chapters?.length || 0} Bagian</span>
                 </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-accent font-bold">
                  {novel.postMeta?.year || new Date(novel.createdAt).getFullYear()}
                </div>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium leading-[1.1] text-foreground group-hover:text-accent transition-colors duration-300">
                {novel.title}
              </h2>
              <div className="flex items-center gap-3">
                 <div className="w-6 h-6 rounded-full bg-muted overflow-hidden border border-border/50">
                    <img src={getImageUrl(novel.author?.avatar)} alt={novel.author?.name} className="w-full h-full object-cover" />
                 </div>
                 <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{novel.author?.name}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {novels?.length === 0 && (
         <div className="py-40 text-center border border-dashed border-border rounded-3xl bg-secondary/10">
            <BookOpen size={64} className="  text-muted-foreground/10 mx-auto mb-8" />
            <h3 className="font-serif text-2xl text-muted-foreground italic">Lemari katalog sedang kosong...</h3>
         </div>
      )}
    </div>
  );
}
